<template>
  <transition name="slide-down">
    <div 
      v-if="visible" 
      class="connection-status-banner" 
      :class="{ 'offline': !isOnline, 'online': isOnline && wasOffline }"
    >
      <div class="banner-content">
        <!-- Offline Icon and Text -->
        <template v-if="!isOnline">
          <i class="fa-solid fa-wifi-slash icon-spin"></i>
          <span class="message-fa">اتصال شما به اینترنت قطع شد. در حال استفاده از نسخه آفلاین...</span>
          <span class="message-en">You are currently offline. Running in offline mode.</span>
        </template>
        
        <!-- Back Online Icon and Text -->
        <template v-else-if="isOnline && wasOffline">
          <i class="fa-solid fa-wifi"></i>
          <span class="message-fa">اتصال با موفقیت برقرار شد!</span>
          <span class="message-en">Back online! Content updated.</span>
        </template>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useConnectionStatus } from '~/utils/_pwa.js'

const { isOnline } = useConnectionStatus()
const visible = ref(false)
const wasOffline = ref(false)
let hideTimeout = null

// Watch connectivity status to show/hide the banner
watch(isOnline, (newStatus) => {
  if (hideTimeout) {
    clearTimeout(hideTimeout)
  }

  if (!newStatus) {
    // User went offline
    wasOffline.value = true
    visible.value = true
  } else {
    // User came back online
    if (wasOffline.value) {
      visible.value = true
      
      // Auto-hide the "Back Online" message after 3.5 seconds
      hideTimeout = setTimeout(() => {
        visible.value = false
        // Reset the tracker after banner completes transition
        setTimeout(() => {
          wasOffline.value = false
        }, 500)
      }, 3500)
    } else {
      visible.value = false
    }
  }
}, { immediate: true })

onMounted(() => {
  // Initialize state based on current status
  if (!navigator.onLine) {
    isOnline.value = false
    wasOffline.value = true
    visible.value = true
  }
})
</script>

<style lang="scss" scoped>
.connection-status-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1051; // Higher than standard navigation/headers
  padding: 10px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
  direction: rtl;

  &.offline {
    background-color: #ea580c; // Warm dark orange/red
    color: #ffffff;
  }

  &.online {
    background-color: #1a9350; // Sarvban green
    color: #ffffff;
  }

  .banner-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    font-size: 0.85rem;
    font-weight: 600;

    @media (min-width: 576px) {
      flex-direction: row;
      gap: 12px;
      font-size: 0.9rem;
    }

    i {
      font-size: 1.1rem;
    }

    .icon-spin {
      animation: pulse 1.8s infinite ease-in-out;
    }

    .message-fa {
      font-family: inherit;
    }

    .message-en {
      font-size: 0.75rem;
      opacity: 0.85;
      font-weight: 400;
      direction: ltr;

      @media (min-width: 576px) {
        font-size: 0.8rem;
        border-right: 1px solid rgba(255, 255, 255, 0.3);
        padding-right: 12px;
      }
    }
  }
}

// Transitions
.slide-down-enter-from,
.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-enter-to,
.slide-down-leave-from {
  transform: translateY(0);
  opacity: 1;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.92); }
}
</style>
